/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {PeriodDetailModel} from '../models/period/period-detail/period-detail.model';
import {TableModel} from '../../shared/table/models/table.model';
import {TableHeaderEntryModel} from '../../shared/table/models/table-header-entry.model';
import {PeriodBundlesTableColumnEnum} from './models/period-bundles-table-column.enum';
import periodBundlesHeadersConfiguration from './configuration/period-bundles-headers-configuration';
import {BundleDetailModel} from '../models/bundle/bundle-detail/bundle-detail.model';
import {TableLineModel} from '../../shared/table/models/table-line.model';
import {TableLineDataModel} from '../../shared/table/models/table-line-data.model';
import {BundlePositionsDetailModel} from '../models/bundle/bundle-positions-detail/bundle-positions-detail.model';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {PeriodBundlesTableStateModel} from './models/period-bundles-table.state.model';
import {VariableColumnsService} from '../services/variable-columns/variable-columns.service';
import {ProgressBarSquareComponent} from '../../shared/progress-bar/progress-bar-square/progress-bar-square.component';
import {PeriodBundlesTableStatusEnum} from './models/period-bundles-table-status.enum';
import {ProgressBarLineComponent} from '../../shared/progress-bar/progress-bar-line/progress-bar-line.component';
import * as PeriodBundlesTableDataPipes from '../period-bundles-table/data-pipes/period-bundles-table-data-pipes';
import {
    closedOnPipe,
    userPipe
} from '../period-bundles-table/data-pipes/period-bundles-table-data-pipes';
import {BundlesConfigService} from '../services/bundles-config.service';
import {InfoChartConfigurationModel} from '../../shared/info-chart/models/info-chart-configuration.model';
import {PeriodBundlesTableInfoChartDataMapper} from './models/mappers/period-bundles-table-info-chart-data.mapper';
import {ClosedOnCellValueModel} from './models/closed-on-cell-value.model';
import {UserBaseModel} from '../models/user-base/user-base.model';
import {ReferenceDataModel} from '../../api/reference-data.model';
import {RetrieveOptionsModel} from '../bundles-main-page/models/retrieve-options.model';
import {BundleInformationTableBundleDataModel} from '../bundle-information-table/models/bundle-information-table-bundle-data.model';
import {PeriodsTableSearchCriteriaService} from '../periods-table/services/periods-table-search-criteria.service';
import {Subscription} from 'rxjs';
import {PeriodsViewsService} from '../services/periods-views/periods-views.service';
import {BundleSettingsFormService} from '../services/bundle-settings-form.service';

type TEnum = PeriodBundlesTableColumnEnum | string;

@Component({
    selector: 'app-period-bundles-table',
    templateUrl: './period-bundles-table.component.html',
    styleUrls: ['./period-bundles-table.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PeriodBundlesTableComponent extends StatefulComponent<PeriodBundlesTableStateModel>
        implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() bundlesList: BundleDetailModel[];
    @Input() periodDetail: PeriodDetailModel;
    @Input() isBundleView = false;

    @Output() refreshTableData = new EventEmitter<RetrieveOptionsModel>();
    @Output() updatePeriodDetailValue = new EventEmitter<PeriodDetailModel>();

    public readonly PERIOD_CRITERIA_TITLE = 'Period criteria';
    public readonly PERIOD_CRITERIA_CYPRESS_TAG = 'period-criteria';
    // Table name
    public readonly TABLE_NAME = 'bundle list';
    @ViewChild('bundlesProgressBarSquaresTemplate') bundlesProgressBarSquaresTemplate: TemplateRef<ProgressBarSquareComponent>;
    @ViewChild('bundlesProgressBarLinesTemplate') bundlesProgressBarLinesTemplate: TemplateRef<ProgressBarLineComponent>;
    @ViewChild('statusInformationTemplate') statusInformationTemplate: TemplateRef<ElementRef>;
    @ViewChild('bundlesTotalSettlementsTemplate') bundlesTotalSettlementsTemplate: TemplateRef<ElementRef>;
    // Additional columns
    private criteriaSpecsCodes: string[] = [];
    private valuesSpecsCodes: string[] = [];
    private readonly ADDITIONAL_CRITERIA_SUFFIX = 'CRITERIA';
    private readonly ADDITIONAL_VALUE_SUFFIX = 'VALUE';
    private readonly BASE_ADDITIONAL_CRITERIA_INSERT_INDEX = 4;
    private readonly BASE_ADDITIONAL_CRITERIA_VIEW_INSERT_INDEX = 2;
    private readonly BASE_ADDITIONAL_VALUE_INSERT_INDEX = 5;
    private readonly PROGRESS_BAR_MAX_SQUARES_BY_LINE = 10;
    private readonly PROGRESS_BAR_MAX_SQUARES_TO_LINE = 20;
    private readonly PROGRESS_BAR_OPEN_STATUS = '#3f7bfe';
    private readonly PROGRESS_BAR_CLOSED_STATUS = '#000000';
    private readonly PROGRESS_BAR_SQUARE_OPEN_STATUS = '#8cb0fe';
    private readonly PROGRESS_BAR_LINE_GRADIENT_OPEN_STATUS
            = 'linear-gradient(90deg, rgb(179, 202, 255) 0%, rgb(63, 123, 254) 100%) rgb(217, 229, 255)';
    private readonly PROGRESS_BAR_LINE_GRADIENT_CLOSED_STATUS
            = 'linear-gradient(90deg, rgb(180, 180, 180) 0%, rgb(0, 0, 0) 100%) rgb(217, 229, 255)';
    // Full line tooltip detail
    private readonly FULL_LINE_TOOLTIP_DETAIL: string = 'bundle line';
    private readonly INFO_CHART_DESCRIPTION: string = 'Period total amount:';
    private readonly tooltipZebraMessage =
            'This bar represents the group of bundles that individually are too small to appear independently';
    private subscriptions = new Subscription();
    readonly backgroundColorHover = '#EFEFEF';

    public tableData: TableModel<TEnum, BundlePositionsDetailModel[]> = {
        headers: {
            entries: new Map<TEnum, TableHeaderEntryModel<TEnum>>([
                [PeriodBundlesTableColumnEnum.NUMBER, periodBundlesHeadersConfiguration.number],
                [PeriodBundlesTableColumnEnum.STATUS, periodBundlesHeadersConfiguration.status],
                [PeriodBundlesTableColumnEnum.CLOSED_BY, periodBundlesHeadersConfiguration.closedBy],
                [PeriodBundlesTableColumnEnum.CLOSED_ON, periodBundlesHeadersConfiguration.closedOn],
                [PeriodBundlesTableColumnEnum.LINES_QUANTITY, periodBundlesHeadersConfiguration.linesQuantity],
                [PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS, periodBundlesHeadersConfiguration.totalCostProgress],
                [PeriodBundlesTableColumnEnum.TOTAL_GROSS_COST, periodBundlesHeadersConfiguration.totalAmount],
                [PeriodBundlesTableColumnEnum.SENT, periodBundlesHeadersConfiguration.sent]
            ])
        },
        expandableLineTooltipDetail: this.FULL_LINE_TOOLTIP_DETAIL,
        expandableLines: true,
        staticSection: []
    };

    public tableDataBundlesView: TableModel<TEnum, BundlePositionsDetailModel[]> = {
        headers: {
            entries: new Map<TEnum, TableHeaderEntryModel<TEnum>>([
                [PeriodBundlesTableColumnEnum.NUMBER, periodBundlesHeadersConfiguration.number],
                [PeriodBundlesTableColumnEnum.STATUS, periodBundlesHeadersConfiguration.status],
                [PeriodBundlesTableColumnEnum.LINES_QUANTITY, periodBundlesHeadersConfiguration.linesQuantity],
                [PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS, periodBundlesHeadersConfiguration.totalCostProgress],
                [PeriodBundlesTableColumnEnum.TOTAL_GROSS_COST, periodBundlesHeadersConfiguration.totalAmount],
                [PeriodBundlesTableColumnEnum.TOTAL_SETTLEMENTS, periodBundlesHeadersConfiguration.totalSettlements]
            ])
        },
        expandableLineTooltipDetail: this.FULL_LINE_TOOLTIP_DETAIL,
        expandableLines: true,
        staticSection: []
    };

    get periodCriteriaInfo(): string[] {
        let periodicity = 'Periodicity: ';
        let maxNumber = 'Maximum bundles quantity: ';

        if (this.periodDetail?.spec == null) {
            periodicity += '-';
            maxNumber += '-';
        } else {
            periodicity += `${this.periodDetail?.spec?.periodicity?.name ?? '-'}`;
            maxNumber += `${this.periodDetail?.spec?.maxBundles ?? 'Unlimited'}`;
        }

        return [periodicity, maxNumber];
    }

    /**
     * Returns the map index where the additional values should be inserted
     */
    get additionalValueInsertIndex(): number {
        return this.isBundleView
                ? this.BASE_ADDITIONAL_CRITERIA_VIEW_INSERT_INDEX + this.criteriaSpecsCodes?.length
                : this.BASE_ADDITIONAL_VALUE_INSERT_INDEX + this.criteriaSpecsCodes?.length;
    }

    /**
     * Returns the map index where the additional spec should be inserted
     */
    get additionalCriteriaInsertIndex(): number {
        return this.isBundleView
                ? this.BASE_ADDITIONAL_CRITERIA_VIEW_INSERT_INDEX
                : this.BASE_ADDITIONAL_CRITERIA_INSERT_INDEX;
    }

    /**
     * Returns if the lines quantity column should be visible
     */
    get hasLinesQuantityColumn(): boolean {
        let hasQuantity;
        if (this.isBundleView) {
            hasQuantity = this.bundlesList?.some((bundle) => this.showBundleLinesQuantityValue(bundle));
        } else {
            hasQuantity = this.periodDetail?.bundlesList.some((bundle) => this.showBundleLinesQuantityValue(bundle));
        }
        return hasQuantity;
    }

    /**
     * Returns if the total cost progress column should be visible
     */
    get hasTotalCostProgressColumn(): boolean {
        let hasTotalCostProgress;
        if (this.isBundleView) {
            hasTotalCostProgress = this.bundlesList?.some((bundle) => bundle?.spec?.maxValue != null);
        } else {
            hasTotalCostProgress = this.periodDetail?.bundlesList.some((bundle) => bundle?.spec?.maxValue != null);
        }
        return hasTotalCostProgress;
    }

    /**
     * Returns the correct accordion context that is stored in the component state
     */
    get accordionContext(): BundlePositionsDetailModel[] | null {
        let accordionData: BundlePositionsDetailModel[] | null = null;
        if (this.state != null && this.state.selectedLine != null) {
            accordionData = this.state.selectedLine.accordionData ?? null;
        }
        return accordionData;
    }

    get tableDataInfo(): TableModel<TEnum, BundlePositionsDetailModel[]> {
        return this.isBundleView ? this.tableDataBundlesView : this.tableData;
    }

    constructor(private variableColumns: VariableColumnsService,
                private bundlesConfigService: BundlesConfigService,
                private search: PeriodsTableSearchCriteriaService,
                private cdr: ChangeDetectorRef,
                private periodsViewsService: PeriodsViewsService,
                private bundleSettingsFormService: BundleSettingsFormService) {
        super();
    }

    ngOnInit(): void {
        // Must be run before retrieveTableData() because all the headers must be in place before mapping the table data
        this.addAdditionalColumnHeaders();

        // If the page is changed we must restore the selected line to default in order to updated the table data
        this.subscriptions.add(
                this.search.changePageEvent.subscribe(() => this.storeState({selectedLine: undefined}))
        );
    }

    ngAfterViewInit(): void {
        this.retrieveTableData();
        this.cdr.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['bundlesList']?.previousValue) {
            if (this.state.selectedLine && this.periodsViewsService.updateTableLinesByGlobalSearchButton) {
                this.periodsViewsService.updateTableLinesByGlobalSearchButton = false;
                this.retrieveTableData();
            } else if (!this.state.selectedLine) {
                this.retrieveTableData();
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    periodTotalAmountInfoChart(): InfoChartConfigurationModel<string> {
        return {
            description: this.INFO_CHART_DESCRIPTION,
            totalValue: this.periodDetail?.totalAmount,
            interactable: true,
            infoData: PeriodBundlesTableInfoChartDataMapper.fromBundleDetailModelToInfoChartDataModelArray(this.periodDetail?.bundlesList),
            tooltip: `${this.FULL_LINE_TOOLTIP_DETAIL}`,
            currency: this.periodDetail?.bundlesList?.[0]?.spec?.currency,
            zebraTooltip: this.tooltipZebraMessage
        };
    }

    mouseHoverLine(identifier: string): void {
        this.tableData.staticSection.map((item) => item.isMouseHover = false);
        const line = this.tableData.staticSection.find((item) => item.identifier === identifier);
        if (line != null) {
            line.isMouseHover = !line.isMouseHover;
        }
    }

    storeLineData(line: TableLineModel<TEnum, BundlePositionsDetailModel[]>, lineData: BundleDetailModel): void {
        this.storeState({
            selectedLine: line,
            selectedLineCriteria: lineData?.spec,
            selectedLineData: this.bundleSelectedLineData(line, lineData),
            selectedLineClosedInformation: {
                closedBy: lineData?.closedBy,
                closedOn: {
                    closedOn: lineData?.closedOn,
                    timeZone: this.bundlesConfigService?.timezone
                }
            },
            selectedLineSentInformation: {
                sent: lineData?.sent,
                sentBy: lineData?.sentBy,
                sentOn: {
                    date: lineData?.sentOn,
                    timeZone: this.bundlesConfigService?.timezone
                }
            }
        });
    }

    openLine(identifier: string): void {
        const line = this.tableData.staticSection?.find((item) => item?.identifier === identifier);
        const lineData = this.periodDetail?.bundlesList?.find((bundle) => bundle?.number === line?.identifier);

        if (line?.isOpen === true) {
            this.tableData.staticSection.forEach((item) => item.isOpen = false);
        } else if (line?.isOpen === false) {
            this.tableData.staticSection.forEach((item) => item.isOpen = false);
            line.isOpen = true;
            this.loadDetailData(line, undefined, true);
        }
        this.storeLineData(line, lineData);
    }

    loadDetailData(line: TableLineModel<TEnum, BundlePositionsDetailModel[]>,
                   bundleDetail?: BundleDetailModel, isChartOpen?: boolean): void {
        // Close opened manually lines
        if (isChartOpen == null && line != null) {
            this.tableData.staticSection.forEach((item) => item.isOpen = false);
            const value = this.tableData.staticSection
                    .find((item) => item.identifier === line.identifier);
            if (value != null) {
                if (this.isBundleView) {
                    const lineDataDetailBundleView =
                            this.bundlesList?.find((bundle) => bundle.number === line.identifier);
                    this.storeLineData(value, lineDataDetailBundleView);
                } else {
                    const lineDataDetail = this.periodDetail?.bundlesList?.find((bundle) => bundle.number === line.identifier);
                    this.storeLineData(value, lineDataDetail);
                }
                value.isOpen = !value.isOpen;
            }
        }
        if (line != null && line.accordionData == null) {
            let lineData = bundleDetail;
            if (lineData == null) {
                if (this.isBundleView) {
                    lineData = this.bundlesList.find((bundle) => bundle.number === line.identifier);
                } else {
                    lineData = this.periodDetail?.bundlesList?.find((bundle) => bundle.number === line.identifier);
                }
            } else {
                line.data = this.mapLineData(lineData);
            }

            if (lineData) {
                line.accordionData = lineData?.bundlePositions;
                this.storeLineData(line, lineData);
            }
        }
    }

    /**
     * Updates de period detail line after the detail has been updated in the child component
     * @param periodDetail to the {@link updatePeriodDetailValue}
     */
    updatePeriodDetail(periodDetail: PeriodDetailModel): void {
        this.updatePeriodDetailValue.emit(periodDetail);
    }

    updateInfoChartClose(identifier: string): void {
        this.storeState({infoChartElementClosed: identifier});
    }

    /**
     * Updates de bundle detail line after the detail has been updated in the child component
     * @param bundleDetail to the {@link BundleDetailModel}
     */
    updateBundleDetail(bundleDetail: BundleDetailModel): void {
        let tableLine;
        if (this.isBundleView) {
            tableLine = this.tableDataBundlesView.staticSection
                    .find((line) => line.identifier === bundleDetail.number);
        } else {
            tableLine = this.tableData.staticSection
                    .find((line) => line.identifier === bundleDetail.number);
        }
        if (tableLine != null) {
            tableLine.accordionData = null;
            this.loadDetailData(tableLine, bundleDetail);
        }
    }

    /**
     * Pipe to a string a number and percentage symbol
     */
    percentageLabel(value: number): string {
        return PeriodBundlesTableDataPipes.percentagePipe(value);
    }

    showBundlesProgressBarSquaresTemplate(totalSquares: number): boolean {
        return totalSquares > 1;
    }

    /**
     * Returns the date when the bundle was closed
     */
    bundleClosedOn(date: number): string {
        const cellValue: ClosedOnCellValueModel = {
            closedOn: date,
            timeZone: this.bundlesConfigService.timezone
        };
        return closedOnPipe(cellValue);
    }

    /**
     * Returns if the bundle is eligible for the tooltip on the status Closed
     */
    displayStatusTooltip(status: ReferenceDataModel): boolean {
        return this.isBundleView && status?.code === PeriodBundlesTableStatusEnum.CLOSED;
    }

    statusClosedTooltip(status: ReferenceDataModel, closedBy: UserBaseModel, closedOn: number): string {
        return `${status?.name} by ${userPipe(closedBy)} on ${this.bundleClosedOn(closedOn)}`;
    }

    getStyleForBundlingSettingsOpen(): string {
        if (this.bundleSettingsFormService.getBundlingSettingsOpen()) {
            return 'open-settings';
        }
    }

    mouseHoverHandler(tableLine: TableLineModel<PeriodBundlesTableColumnEnum, PeriodDetailModel>): void {
        this.storeState({mouseHoverLineIdentifier: tableLine?.identifier});
    }

    /**
     * Returns BundleInformationTableBundleDataModel based on the bundle view
     * On the bundle view, as we have more than one period we need to get the correct period detail were the bundle belongs
     * If not on the bundle view, the period detail already is passed on the input
     */
    private bundleSelectedLineData(line: TableLineModel<TEnum, BundlePositionsDetailModel[]>,
                                   lineData: BundleDetailModel): BundleInformationTableBundleDataModel {
        let selectedLineData: BundleInformationTableBundleDataModel;
        if (this.isBundleView) {
            const detail = this.periodsViewsService.getPeriodDetailByBundleNumber(lineData.number);
            selectedLineData = PeriodBundlesTableInfoChartDataMapper
                    .fromBundleDetailModelToBundleInformationTableBundleDataModel(lineData, detail, line);
        } else {
            selectedLineData = PeriodBundlesTableInfoChartDataMapper
                    .fromBundleDetailModelToBundleInformationTableBundleDataModel(lineData, this.periodDetail, line);
        }

        return selectedLineData;
    }

    /**
     * Returns if for a specific bundle the lines quantity value should be displayed
     */
    private showBundleLinesQuantityValue(bundle: BundleDetailModel): boolean {
        return bundle?.spec?.maxPositions > 1;
    }

    /**
     * Returns the lines quantity cell value for a given bundle
     */
    private retrieveBundleLinesQuantityCellValue(bundle: BundleDetailModel): string {
        let cellValue = '-';
        if (this.showBundleLinesQuantityValue(bundle)) {
            cellValue = `${bundle.bundlePositions.length} line(s)`;
        }
        return cellValue;
    }

    /**
     * Retrieves the period-bundles list data and populates de staticData array with the current bundles
     */
    private retrieveTableData(): void {
        if (this.isBundleView) {
            this.tableDataBundlesView.staticSection = this.mapData(this.bundlesList);
            this.tableDataBundlesView.noFilterResultsFoundTemplate = this.periodsViewsService.isGlobalSearchCriteriaApplied();
        } else {
            this.tableData.staticSection = this.mapData(this.periodDetail?.bundlesList);
        }
    }

    /**
     * Converts a {@link BundleDetailModel[]} to a {@link TableLineDataModel}
     * @param bundlesListData the {@link BundleDetailModel[]} to convert
     */
    private mapData(bundlesListData: BundleDetailModel[]): TableLineModel<PeriodBundlesTableColumnEnum, BundlePositionsDetailModel[]>[] {
        this.updateDynamicColumnsHeaders();
        let lines: TableLineModel<PeriodBundlesTableColumnEnum, BundlePositionsDetailModel[]>[] = [];
        if (bundlesListData != null) {
            lines = bundlesListData?.map((line: BundleDetailModel) => ({
                isOpen: false,
                identifier: line.number,
                data: this.mapLineData(line)
            } as TableLineModel<PeriodBundlesTableColumnEnum, BundlePositionsDetailModel[]>));
        }
        return lines;
    }

    /**
     * Maps a {@link BundleDetailModel} to a Map<PeriodBundlesTableColumnEnum, TableLineDataModel>
     */
    private mapLineData(data: BundleDetailModel): Map<TEnum, TableLineDataModel> {
        let map: Map<TEnum, TableLineDataModel> = new Map<TEnum, TableLineDataModel>([]);
        if (this.isBundleView) {
            map = new Map<TEnum, TableLineDataModel>([
                [
                    PeriodBundlesTableColumnEnum.NUMBER, {
                    cellValue: data?.number
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.STATUS, {
                    cellValue: {
                        data: data?.status,
                        templateRef: this.statusInformationTemplate,
                        context: {
                            bundleInformation: {
                                closedTooltip: this.statusClosedTooltip(data?.status, data?.closedBy, data?.closedOn),
                                status: data?.status
                            }
                        }
                    }
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.LINES_QUANTITY, {
                    cellValue: {
                        templateRef: this.showProgressBarLine(data?.spec?.maxPositions) ?
                                this.bundlesProgressBarLinesTemplate : this.bundlesProgressBarSquaresTemplate,
                        context: this.mapProgressBar(data)
                    }
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS, {
                    cellValue: {
                        templateRef: this.bundlesProgressBarLinesTemplate,
                        context: {
                            progressBar: {
                                percentage: data?.spec?.maxValue != null ? data?.totalCostProgress : null,
                                dotBackgroundColor: this.progressBarLineBackgroundDot(data?.status?.code),
                                lineBackgroundColor: this.progressBarLineBackgroundLine(data?.status?.code)
                            }
                        }
                    }
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.TOTAL_GROSS_COST, {
                    cellValue: {
                        data: data?.totalValue,
                        currency: data?.spec?.currency
                    },
                    copyPasteValue: data?.totalValue
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.TOTAL_SETTLEMENTS, {
                    cellValue: {
                        templateRef: this.bundlesTotalSettlementsTemplate,
                        context: {
                            totalSettlements: data.totalSettlements
                        }
                    }
                }
                ]
            ]);
        } else {
            map = new Map<TEnum, TableLineDataModel>([
                [
                    PeriodBundlesTableColumnEnum.NUMBER, {
                    cellValue: data?.number
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.STATUS, {
                    cellValue: {
                        data: data?.status,
                        templateRef: this.statusInformationTemplate,
                        context: {
                            bundleInformation: {
                                closedTooltip: this.statusClosedTooltip(data?.status, data?.closedBy, data?.closedOn),
                                status: data?.status
                            }
                        }
                    }
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.CLOSED_BY, {
                    cellValue: data?.closedBy
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.CLOSED_ON, {
                    cellValue: {
                        closedOn: data?.closedOn,
                        timeZone: this.bundlesConfigService?.timezone
                    }
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.LINES_QUANTITY, {
                    cellValue: {
                        templateRef: this.showProgressBarLine(data?.spec?.maxPositions) ?
                                this.bundlesProgressBarLinesTemplate : this.bundlesProgressBarSquaresTemplate,
                        context: this.mapProgressBar(data)
                    }
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS, {
                    cellValue: {
                        templateRef: this.bundlesProgressBarLinesTemplate,
                        context: {
                            progressBar: {
                                percentage: data?.spec?.maxValue != null ? data?.totalCostProgress : null,
                                dotBackgroundColor: this.progressBarLineBackgroundDot(data?.status?.code),
                                lineBackgroundColor: this.progressBarLineBackgroundLine(data?.status?.code)
                            }
                        }
                    }
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.TOTAL_GROSS_COST, {
                    cellValue: {
                        data: data?.totalValue,
                        currency: data?.spec?.currency
                    },
                    copyPasteValue: data?.totalValue
                }
                ],
                [
                    PeriodBundlesTableColumnEnum.SENT, {
                    cellValue: {
                        closed: data?.status?.code,
                        sent: data?.sent
                    }
                }
                ]
            ]);
        }
        // Must be run before the removeHiddenColumnsValues() to ensure the values are injected in the right index
        this.addAdditionalColumnValues(map, data);
        this.removeHiddenColumnsValues(map);
        return map;
    }

    /**
     * Maps a {@link BundleDetailModel} to a Squares or Line progress bar template model
     */
    private mapProgressBar(data: BundleDetailModel): Object {
        let progressBarData;
        if (this.showProgressBarLine(data?.spec?.maxPositions)) {
            progressBarData = {
                progressBar: {
                    percentage: this.getProgressBarLinePercentage(data?.bundlePositions?.length, data?.spec?.maxPositions),
                    dotBackgroundColor: this.progressBarLineBackgroundDot(data?.status?.code),
                    lineBackgroundColor: this.progressBarLineBackgroundLine(data?.status?.code)
                }
            };
        } else {
            progressBarData = {
                squares: {
                    squaresActive: data?.bundlePositions?.length,
                    squaresBackgroundActive: this.progressBarSquareBackground(data?.status?.code),
                    maxLineSquares: this.progressBarTotalSquares(data?.spec?.maxPositions),
                    totalSquares: data?.spec?.maxPositions
                },
                lines: this.retrieveBundleLinesQuantityCellValue(data)
            };

        }
        return progressBarData;
    }

    /**
     * Retrieve the percentage number to be used on a progress bar (line)
     */
    private getProgressBarLinePercentage(bundlesPositions: number, maxPositions: number): number {
        return bundlesPositions != null && maxPositions != null ? Math.round((bundlesPositions / maxPositions) * 100) : 0;
    }

    /**
     * Check if maxPositions > PROGRESS_BAR_MAX_SQUARES_TO_LINE
     */
    private showProgressBarLine(maxPositions: number): boolean {
        return maxPositions > this.PROGRESS_BAR_MAX_SQUARES_TO_LINE;
    }

    /**
     * Retrieve the max squares by line
     */
    private progressBarTotalSquares(maxPositions: number): number {
        return maxPositions > this.PROGRESS_BAR_MAX_SQUARES_BY_LINE ? this.PROGRESS_BAR_MAX_SQUARES_BY_LINE : maxPositions;
    }

    /**
     * Retrieve background-color of the progress bar squares
     */
    private progressBarSquareBackground(status: string): string {
        return <PeriodBundlesTableStatusEnum>status === PeriodBundlesTableStatusEnum.OPEN
                ? this.PROGRESS_BAR_SQUARE_OPEN_STATUS : this.PROGRESS_BAR_CLOSED_STATUS;
    }

    /**
     * Retrieve the background-color of the line progress bar dot
     */
    private progressBarLineBackgroundDot(status: string): string {
        return <PeriodBundlesTableStatusEnum>status === PeriodBundlesTableStatusEnum.OPEN
                ? this.PROGRESS_BAR_OPEN_STATUS : this.PROGRESS_BAR_CLOSED_STATUS;
    }

    private progressBarLineBackgroundLine(status: string): string {
        return <PeriodBundlesTableStatusEnum>status === PeriodBundlesTableStatusEnum.OPEN ?
                this.PROGRESS_BAR_LINE_GRADIENT_OPEN_STATUS : this.PROGRESS_BAR_LINE_GRADIENT_CLOSED_STATUS;
    }

    /**
     * Adds or removes the dynamic columns headers (LINES_QUANTITY and TOTAL_COST_PROGRESS)
     */
    private updateDynamicColumnsHeaders(): void {
        if (!this.hasLinesQuantityColumn) {
            this.tableData.headers.entries.delete(PeriodBundlesTableColumnEnum.LINES_QUANTITY);
            this.tableDataBundlesView.headers.entries.delete(PeriodBundlesTableColumnEnum.LINES_QUANTITY);
        } else if (!this.tableData.headers.entries.has(PeriodBundlesTableColumnEnum.LINES_QUANTITY)) {
            this.tableData.headers.entries.set(PeriodBundlesTableColumnEnum.LINES_QUANTITY,
                    periodBundlesHeadersConfiguration.linesQuantity);
            this.tableDataBundlesView.headers.entries.set(PeriodBundlesTableColumnEnum.LINES_QUANTITY,
                    periodBundlesHeadersConfiguration.linesQuantity);
        }

        if (!this.hasTotalCostProgressColumn) {
            this.tableData.headers.entries.delete(PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS);
            this.tableDataBundlesView.headers.entries.delete(PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS);
        } else if (!this.tableData.headers.entries.has(PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS)) {
            this.tableData.headers.entries.set(PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS,
                    periodBundlesHeadersConfiguration.totalCostProgress);
            this.tableDataBundlesView.headers.entries.set(PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS,
                    periodBundlesHeadersConfiguration.totalCostProgress);
        }
    }

    /**
     * Removes the dynamic columns values from the value map (LINES_QUANTITY and TOTAL_COST_PROGRESS)
     */
    private removeHiddenColumnsValues(map: Map<TEnum, TableLineDataModel>): void {
        if (!this.hasLinesQuantityColumn) {
            map.delete(PeriodBundlesTableColumnEnum.LINES_QUANTITY);
        }

        if (!this.hasTotalCostProgressColumn) {
            map.delete(PeriodBundlesTableColumnEnum.TOTAL_COST_PROGRESS);
        }
    }

    /**
     * Adds the additional spec and value headers to the table if is bundle view or not
     */
    private addAdditionalColumnHeaders(): void {
        const bundleDetailData: BundleDetailModel[] = this.isBundleView
                ? this.bundlesList
                : this.periodDetail?.bundlesList;
        // Add additional spec column headers
        this.variableColumns.addVariableColumnsHeaders(
                this.tableDataInfo.headers.entries,
                this.variableColumns.getCriteriaSpecs(bundleDetailData),
                this.criteriaSpecsCodes,
                periodBundlesHeadersConfiguration.additionalCriterion,
                this.additionalCriteriaInsertIndex,
                this.ADDITIONAL_CRITERIA_SUFFIX);
        // Add additional values column headers
        this.variableColumns.addVariableColumnsHeaders(
                this.tableDataInfo.headers.entries,
                this.variableColumns.getValuesSpecs(bundleDetailData),
                this.valuesSpecsCodes,
                periodBundlesHeadersConfiguration.additionalValue,
                this.additionalValueInsertIndex,
                this.ADDITIONAL_VALUE_SUFFIX);
    }

    /**
     * Adds the additional spec and value values to a table line
     * @param tableLine the table line into which the values must be added
     * @param data the {@link BundleDetailModel} containing the line value info
     */
    private addAdditionalColumnValues(tableLine: Map<TEnum, TableLineDataModel>, data: BundleDetailModel): void {
        // Add additional spec column values
        this.variableColumns.addVariableColumnsValues(tableLine, this.criteriaSpecsCodes, this.additionalCriteriaInsertIndex,
                data.criteria, this.ADDITIONAL_CRITERIA_SUFFIX);
        // Add additional values column values
        this.variableColumns.addVariableColumnsValues(tableLine, this.valuesSpecsCodes, this.additionalValueInsertIndex,
                data.values, this.ADDITIONAL_VALUE_SUFFIX, data.spec?.currency);
    }
}
